import { If } from 'aa_common/front-end/components'
import InputNumber from 'aa_common/front-end/components/form/form-input-number'
import { everyTrue, ifElse } from 'aa_common/front-end/helpers'
import { Col, Row } from 'antd'
import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { getDMethod, isPartialImpairment } from 'common/helpers'
import { useNextEffect } from 'common/hooks/use-next-effect'
import { useSetDefaultFormValue } from 'common/hooks/use-set-default-form-value'
import { getNs } from 'common/i18n-config'
import { LedgerSettingsItem } from 'common/models'
import Form from 'components/molecules/Form'
import { useFormContext } from 'react-hook-form'

import { useGetAssetLedger } from '../hooks/use-get-asset-ledger'
import { useIsFullImpairment } from '../hooks/use-is-full-impairment'
import { useResidualServiceLifeDefault } from '../hooks/use-residual-service-life-default'
import { SmallInputClass } from '../styles'
import { FIELD_NAMES } from '../validation'

const { FormField } = Form

const { t, useNsReady } = getNs(['components/organisms/assets/asset-impairment', 'common'])

const FieldName = FIELD_NAMES.impair_service_life

type ResidualServiceLifeProps = {
  ledgerSetting?: LedgerSettingsItem
}

export const ResidualServiceLife = ({ ledgerSetting }: ResidualServiceLifeProps) => {
  useNsReady()
  const { setValue } = useFormContext()
  const assetLedger = useGetAssetLedger(ledgerSetting)
  const dMethodCode = getDMethod(assetLedger, true)
  const didImpairment = isPartialImpairment(assetLedger)
  const shouldShow = Boolean(
    dMethodCode &&
    ![
      DEPRECIATION_METHOD_CODE.AVERAGE_USAGE_PERIOD,
      DEPRECIATION_METHOD_CODE.LEASE_PERIOD_STRAIGHT_LINE,
      DEPRECIATION_METHOD_CODE.BULK_EQUAL_INSTALLMENT_METHOD,
      DEPRECIATION_METHOD_CODE.NON_DEPRECIABLE,
    ].includes(dMethodCode)
  )
  const { isFullImpairment: disabled } = useIsFullImpairment()
  const defaultValue = useResidualServiceLifeDefault(
    ifElse(didImpairment, assetLedger?.original_service_life, assetLedger?.service_life)
  )

  useNextEffect(() => {
    !shouldShow && setValue(FieldName, null)
  }, [setValue, shouldShow])
  useNextEffect(() => {
    shouldShow && !disabled && setValue(FieldName, defaultValue)
    shouldShow && disabled && setValue(FieldName, null)
  }, [shouldShow, disabled, setValue, defaultValue])

  const setDefaultValue = useSetDefaultFormValue(FieldName, 1, everyTrue(!disabled, shouldShow))

  return (
    <If condition={shouldShow}>
      <FormField title={t('residual_service_life')} required={!disabled} disabled={disabled}>
        <If condition={disabled}>
          <span className="disabled-content">ー</span>
        </If>
        <If condition={!disabled}>
          <Row align="middle" gutter={8}>
            <Col>
              <InputNumber min={1} name={FieldName} className={SmallInputClass} controls onBlur={setDefaultValue} />
            </Col>
            <Col>
              <span>{t('residual_service_life_unit')}</span>
            </Col>
          </Row>
        </If>
      </FormField>
    </If>
  )
}
